import { Box, Menu, MenuItem as MuiMenuItem, Typography } from "@mui/material";
import styled from "styled-components";

export const SidebarContainer = styled.aside`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: min-width 0.3s ease-in-out;

  &.extended {
    min-width: 250px;
    max-width: 42px;
    width: 250px;
  }

  &.minimized {
    min-width: 42px;
    max-width: 42px;
    width: 42px;
  }
`;

export const SidebarHeader = styled.header`
  height: 60px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette?.borderColor?.default};
  display: flex;
  gap: 10px;
`;

export const SidebarHeaderBox = styled.div`
  display: flex;
  align-items: center;
`;

export const MainHeader = styled(Typography)`
  text-wrap: nowrap;
`;

export const MenuWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  /* Fill the space between header and footer and scroll internally, so a tall
     nav (many items, or larger touch targets on a short/landscape screen)
     never pushes the profile/logout off the bottom. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const MenuItem = styled.div`
  width: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  border-inline-start: 3px solid transparent;
  border-block: 1px solid transparent;
  transition: border-color 0.3s;

  &.active {
    border-inline-start: 3px solid
      ${({ theme }) => theme.palette?.primary?.main};
  }
`;

export const MenuLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  width: 90%;
  height: 28px;
  border-radius: 8px;
  padding-inline: 10px;
  transition: background-color 0.2s ease;

  /* background.secondary is ~invisible against the sidebar's own surface; tint
     with the same accent the active item's border uses instead. */
  &:hover {
    background-color: ${({ theme }) => theme.palette?.primary?.main}14;
  }

  /* Comfortable finger target on touch devices (28px is too short). */
  @media (max-width: 768px) {
    height: 44px;
  }
`;

export const MenuIcon = styled.img`
  height: 18px;
  width: 18px;
  filter: ${({ theme }) => theme.palette?.mode === "dark" && "invert(1)"};
`;

export const FooterContainer = styled.footer`
  margin-top: 10px;
  width: 100%;
  display: flex;
  gap: 10px;
`;

export const ProfileWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding-block: 12px;
  border-top: 1px solid ${({ theme }) => theme.palette?.borderColor?.default};
`;

export const ProfileLink = styled(MenuLink)`
  height: 44px;
  cursor: pointer;

  &.minimized {
    padding-inline: 5px;
  }
`;

export const Circle = styled.div`
  height: 30px;
  width: 30px;
  min-height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette?.background?.secondary};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  background-color: ${({ $background, theme }) =>
    ($background || theme.palette?.background?.secondary) + "80"};
  border: 1px solid ${({ theme }) => theme.palette?.borderColor?.default};
`;

export const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 140px;
`;

export const RightIcon = styled.img`
  height: 18px;
  width: 18px;
  margin-inline-start: auto;
  filter: ${({ theme }) => theme.palette?.mode === "dark" && "invert()"};

  &.circled {
    position: relative;
    left: 24px;
    z-index: 100;
    padding: 3px;
    width: auto;
    height: auto;
    border-radius: 50%;
    background-color: #ffffff;
    border: 1px solid ${({ theme }) => theme.palette?.current}20;
    transform: rotate(90deg);
    cursor: pointer;

    &.minimized {
      left: -24px;
      transform: rotate(-90deg);
    }

    &.rtl {
      right: 4px;
      transform: rotate(-90deg);

      &.minimized {
        right: -24px;
        transform: rotate(90deg);
      }
    }
  }
`;

export const Logo = styled.img`
  height: 24px;
  width: 24px;
  margin-inline-end: 10px;
`;

export const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    width: 225px;
  }
`;

export const StyledMenuItem = styled(MuiMenuItem)``;

export const StyleParentIcon = styled(Box)`
  color: ${({ theme }) => theme.palette?.borderColor?.dark};
  border-left: 1px solid ${({ theme }) => theme.palette.divider};
  height: 100%;
  width: 1px;
`;

/* Tooltips portal out of the sidebar, so they can't inherit its styling — dress
   them in the same surface tokens instead of MUI's default dark bubble. */
export const navTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "borderColor.dark",
      borderRadius: "8px",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
      fontSize: "0.8125rem",
      fontWeight: 500,
      px: 1.25,
      py: 0.75,
    },
  },
};

export const NoWrapTypography = styled(Typography)`
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
